import api from '@/utils/api';

const NAMESPACE = 'org';

export const fetchResearchItems = async (options) => {
  const { researchUUID } = options;
  return await api.get(`${NAMESPACE}/research-items/?research_uuid=${researchUUID}`)
}

export const fetchResearchReviewSessions = async (researchUUID, queryParams = {}) => (
  await api.get(
    `${NAMESPACE}/research-review-sessions/`,
    {
      research_uuid: researchUUID,
      ...queryParams,
    },
  )
);

export const fetchResearchRespondentProfile = async (researchUUID, queryParams = {}) => (
  await api.get(`${NAMESPACE}/researches/${researchUUID}/respondent-profile/`, queryParams)
);

export const researchExports = {
  create: async (researchUUID, fileType) => (
    await api.post(
      `${NAMESPACE}/research-exports/`,
      {
        research_uuid: researchUUID,
        file_type: fileType,
      },
    )
  ),
};

export const validateResearch = async (researchUUID) => (
  await api.get(`${NAMESPACE}/researches/${researchUUID}/validate/`)
);

export const fetchOrganisationDocument = async (organisationFileId) => {
  return await api.get(`${NAMESPACE}/organisation-documents/${organisationFileId}/`)
};


export const organisations = {
  // The organisations on the user's chooser; with `partnerId`, the brands of
  // that partner.
  list: async ({ partnerId } = {}) => {
    const params = {};
    if (partnerId) params.partner_id = partnerId;
    return await api.get(`${NAMESPACE}/organisations/`, params);
  },
  // One organisation, addressed by its uuid;
  get: async (organisationUuid) => (
    await api.get(`${NAMESPACE}/organisations/${organisationUuid}/`)
  ),
  update: async (organisationUuid, formData) => (
    await api.patch(`${NAMESPACE}/organisations/${organisationUuid}/`, formData)
  ),
  updateBanner: async (organisationUuid, formData) => (
    await api.patch(
      `${NAMESPACE}/organisations/${organisationUuid}/banner/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
  ),
  updateLogo: async (organisationUuid, formData) => (
    await api.patch(
      `${NAMESPACE}/organisations/${organisationUuid}/logo/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
  ),
  statistics: async (organisationUuid) => (
    await api.get(`${NAMESPACE}/organisations/${organisationUuid}/statistics/`)
  ),
};


// A partner's admin adds a new brand; linking an existing organisation is
// done by Stramigo in the admin environment.
export const organisationPartnerships = {
  create: async ({ partnerId, brandName }) => (
    await api.post(`${NAMESPACE}/organisation-partnerships/`, {
      partner_id: partnerId,
      brand_name: brandName,
    })
  ),
};

export const organisationMembers = {
  update: async (memberId, { primary, isOnboarded, isAdmin, isActive }) => {
    const params = {};
    if (primary) {
      params.primary = primary;
    }
    if (isOnboarded) {
      params.is_onboarded = isOnboarded;
    }
    if (typeof isAdmin === 'boolean') {
      params.is_admin = isAdmin;
    }
    if (typeof isActive === 'boolean') {
      params.is_active = isActive;
    }
    return await api.patch(
      `${NAMESPACE}/organisation-members/${memberId}/`, params
    )
  },
  list: async (options) => {
    const { organisationId } = options;
    return await api.get(
      `${NAMESPACE}/organisation-members/`,
      { organisation_id: organisationId },
    );
  },
  delete: async (memberId) => {
    return await api.delete(`${NAMESPACE}/organisation-members/${memberId}/`);
  },
};

export const organisationMemberInvitations = {
  list: async ({ organisationId }) => (
    await api.get(
      `${NAMESPACE}/organisation-member-invitations/`,
      { organisation_id: organisationId },
    )
  ),
  create: async ({ organisationId, email, firstName, lastName }) => (
    await api.post(
      `${NAMESPACE}/organisation-member-invitations/`,
      {
        organisation_id: organisationId,
        email,
        first_name: firstName,
        last_name: lastName,
      },
    )
  ),
  get: async (uuid) => (
    await api.get(`public/organisation-member-invitations/${uuid}/`)
  ),
  accept: async (uuid, { firstName, lastName, password, passwordRepeat }) => (
    await api.post(
      `public/organisation-member-invitations/${uuid}/accept/`,
      {
        first_name: firstName,
        last_name: lastName,
        password,
        password_repeat: passwordRepeat,
      },
    )
  ),
  retract: async (uuid) => (
    await api.post(`${NAMESPACE}/organisation-member-invitations/${uuid}/retract/`)
  ),
};

export const deleteOrganisationDocument = async (organisationFileId) => (
  await api.delete(`${NAMESPACE}/organisation-documents/${organisationFileId}/`)
)

export const fetchChatMessageSuggestions = async (options) => {
  const { researchId } = options;
  const params = {
    type: 'chat_suggestion',
  };
  if (researchId) {
    params.research_id = researchId;
  }
  return await api.get(
    `org/ai-prompts/`,
    params,
  )
};

export const organisationDocuments = {
  create: async ({
    organisationId,
    assistantId,
    title,
    file,
    description,
    researchIds = [],
    researchProposalIds = [],
  }) => {
    // One form field per id, as DRF reads a list from a multipart form;
    // axios would send `research_ids[]`;
    const formData = new FormData();
    Object.entries({
      organisation_id: organisationId,
      assistant_id: assistantId,
      title,
      file,
      description,
    }).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });
    researchIds.forEach((id) => formData.append('research_ids', id));
    researchProposalIds.forEach((id) => formData.append('research_proposal_ids', id));
    return await api.post(
      `${NAMESPACE}/organisation-documents/`,
      formData,
      {},
      {
        'Content-Type': 'multipart/form-data',
      },
    )
  },
  delete: async (organisationDocumentUuid) => {
    return await api.delete(
      `${NAMESPACE}/organisation-documents/${organisationDocumentUuid}/`,
    );
  },
  get: async (organisationDocumentUuid) => {
    return await api.get(`${NAMESPACE}/organisation-documents/${organisationDocumentUuid}/`);
  },
  list: async ({
    search,
    organisationId,
    pageSize,
    page,
    researchId,
    researchProposalId,
    relatedToResearchId,
    relatedToResearchProposalId,
  }) => {
    const params = {};

    if (!organisationId) {
      // Returning an error-shaped response keeps the caller's `status !== 200`
      // guard working; returning nothing would crash on `response.status`.
      console.error('organisationDocuments.list: organisation id is required');
      return { status: 0, data: null };
    }
    if (organisationId) {
      params.organisation_id = organisationId;
    }
    if (search) {
      params.search = search;
    }
    if (researchId) {
      params.research_id = researchId;
    }
    if (researchProposalId) {
      params.research_proposal_id = researchProposalId;
    }
    // What a research's or a proposal's page shows: also the documents of its
    // proposal, resp. of its researches;
    if (relatedToResearchId) {
      params.related_to_research_id = relatedToResearchId;
    }
    if (relatedToResearchProposalId) {
      params.related_to_research_proposal_id = relatedToResearchProposalId;
    }
    if (pageSize) {
      params.page_size = pageSize;
    }
    if (page) {
      params.page = page;
    }
    return await api.get(
      `${NAMESPACE}/organisation-documents/`,
      params,
    )
  },
  update: async (organisationFileId, formData) => {
    const { description } = formData;
    return await api.patch(
      `org/organisation-documents/${organisationFileId}/`,
      { description },
    )
  },
  // Links more: a given list has to keep the current links, as only staff
  // unlink, in the admin; leave one out to keep it as it is;
  updateLinks: async (organisationDocumentUuid, { researchIds, researchProposalIds }) => {
    const data = {};
    if (researchIds) {
      data.research_ids = researchIds;
    }
    if (researchProposalIds) {
      data.research_proposal_ids = researchProposalIds;
    }
    return await api.patch(
      `${NAMESPACE}/organisation-documents/${organisationDocumentUuid}/`,
      data,
    )
  },
}

export const chatMessages = {
  update: async (messageId, { status, comment }) => {
    return await api.patch(
      `org/chat-messages/${messageId}/`,
      { feedback_status: status, feedback_comment: comment },
    );
  },
  create: async (threadUUID, payload) => {
    const body = typeof payload === 'string'
      ? { content: payload }
      : payload;
    return await api.post(
      `org/chat-messages/?thread=${threadUUID}`,
      body,
    );
  },
  triggerReply: async (messageId, { threadUUID, version }) => {
    const queryParams = {
      thread: threadUUID,
    }
    if (version) queryParams.version = version

    return await api.get(
      `org/chat-messages/${messageId}/reply/`,
      queryParams,
    );
  },
};

export const chatThreads = {
  // The organisation on screen goes along, so switching organisations in
  // another tab cannot move this intake to a different one; `message` is the
  // user's opening question (else the assistant opens with its own).
  startResearchProposalIntake: async (organisationId, message) => {
    const payload = {};
    if (organisationId) payload.organisation_id = organisationId;
    if (message) payload.message = message;
    return await api.post('org/chat-threads/research-proposal-intake/', payload)
  },
  getLastestOrNew: async (options) => {
    const {
      assistantId,
      title,
      researchUUID,
      scope,
    } = options;
    const payload = { assistant_id: assistantId, title };
    if (researchUUID) {
      payload.research_uuid = researchUUID;
    }
    if (scope) {
      payload.scope = scope;
    }
    return await api.post(
      'org/chat-threads/latest-or-new/',
      payload,
    )
  },
  get: async (threadUUID, options = {}) => {
    const { scope } = options;
    const params = {};
    if (scope) {
      params.scope = scope;
    }
    return await api.get(
      `org/chat-threads/${threadUUID}/`,
      params,
    )
  },
  // Short-lived: fetch one right before connecting to the thread's socket.
  socketToken: async (threadUUID) => {
    return await api.post(`org/chat-threads/${threadUUID}/socket-token/`)
  },
  init: async (threadUUID, { threadMode, researches, version }) => {
    return await api.patch(
      `org/chat-threads/${threadUUID}/init/`,
      {
        mode: threadMode,
        researches,
        version,
      },
    )
  },
  list: async (options) => {
    const {
      organisationId,
      pageSize,
      scope,
      page,
      mode,
      status,
      unfinishedProposal,
    } = options;
    const params = {};
    if (organisationId) {
      params.organisation_id = organisationId;
    }
    if (pageSize) {
      params.page_size = pageSize;
    }
    if (scope) {
      params.scope = scope;
    }
    if (page) {
      params.page = page;
    }
    if (mode) {
      params.mode = mode;
    }
    if (status) {
      params.status = status;
    }
    if (typeof unfinishedProposal === 'boolean') {
      params.unfinished_proposal = unfinishedProposal;
    }
    return await api.get(`org/chat-threads/`, params);
  },
  update: async (threadUUID, formData) => {
    const { title, researches, status } = formData;
    const data = {};
    // An empty title is sent too, so the backend rejects it instead of the
    // rename silently doing nothing.
    if (title !== undefined && title !== null) {
      data.title = title;
    }
    if (researches) {
      data.researches = researches.join(',');
    }
    if (status) {
      data.status = status;
    }
    return await api.patch(
      `org/chat-threads/${threadUUID}/`,
      data,
    )
  },
}

export const researches = {
  createFromBuilder: async (formData) => (
    await api.post(`${NAMESPACE}/researches/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  ),
  get: async (researchUUID) => (
    await api.get(`${NAMESPACE}/researches/${researchUUID}/`)
  ),
  listSetupVerifications: async (researchUUID) => (
    await api.get(`${NAMESPACE}/researches/${researchUUID}/setup-verifications/`)
  ),
  verifySetup: async (researchUUID) => (
    await api.post(`${NAMESPACE}/researches/${researchUUID}/verify-setup/`)
  ),
  update: async (researchUUID, payload) => (
    await api.patch(`${NAMESPACE}/researches/${researchUUID}/`, payload)
  ),
  submit: async (researchUUID, payload) => (
    await api.patch(`${NAMESPACE}/researches/${researchUUID}/submit/`, payload)
  ),
  getPriceEstimate: async (researchUUID, groupSize) => {
    const params = {};
    if (typeof groupSize === 'number') {
      params.group_size = groupSize;
    }
    return await api.get(`${NAMESPACE}/researches/${researchUUID}/price-estimate/`, params);
  },
  getDurationEstimate: async (researchUUID) => (
    await api.get(`${NAMESPACE}/researches/${researchUUID}/duration-estimate/`)
  ),
  create: async ({
    organisationId,
    proposalId,
    title,
    description,
    groupSize,
    researchType,
    startDate,
    selfShared,
  }) => {
    return await api.post(`${NAMESPACE}/researches/`, {
      organisation_id: organisationId,
      proposal_id: proposalId || null,
      title,
      description: description || '',
      group_size: groupSize,
      research_type: researchType,
      start_date: startDate || null,
      // A research with the organisation's own respondents (FID-3.6);
      self_shared: selfShared || undefined,
    });
  },
  // Only a self-shared research: put it live, which can't be undone. With
  // endDate and maxUses it also creates an invite link (`invite_link`);
  publish: async (researchUUID, { endDate, maxUses } = {}) => (
    await api.post(`${NAMESPACE}/researches/${researchUUID}/publish/`, {
      end_date: endDate,
      max_uses: maxUses,
    })
  ),
  list: async ({
    organisationId,
    partnerId,
    excludedStatuses,
    statuses,
    threadUuid,
    search,
    year,
    pageSize,
    page,
  }) => {
    const params = {};

    if (organisationId) {
      params.organisation_id = organisationId;
    }
    if (partnerId) {
      params.partner_id = partnerId;
    }
    if (excludedStatuses) {
      params.statuses_excluded = excludedStatuses.join(',');
    }
    if (statuses && statuses.length) {
      params.statuses = statuses.join(',');
    }
    if (search) {
      params.search = search;
    }
    if (year) {
      params.year = year;
    }
    if (pageSize) {
      params.page_size = pageSize;
    }
    if (threadUuid) {
      params.thread_uuid = threadUuid;
    }
    if (page) {
      params.page = page;
    }

    return await api.get('org/researches/', params);
  },
}

export const items = {
  list: async ({
    organisationId,
    researchTypeSlug,
    search,
    showAll,
    includeGlobal,
    includeAllRenderTypes,
    includeUsedByOrganisation,
    ordering,
    pageSize,
  }) => {
    const params = {};
    if (organisationId) {
      params.organisation_id = organisationId;
    }
    if (researchTypeSlug) {
      params.research_type_slug = researchTypeSlug;
    }
    if (search) {
      params.search = search;
    }
    if (typeof showAll === 'boolean') {
      params.show_all = showAll;
    }
    if (typeof includeGlobal === 'boolean') {
      params.include_global = includeGlobal;
    }
    if (typeof includeAllRenderTypes === 'boolean') {
      params.include_all_render_types = includeAllRenderTypes;
    }
    if (typeof includeUsedByOrganisation === 'boolean') {
      params.include_used_by_organisation = includeUsedByOrganisation;
    }
    if (ordering) {
      params.ordering = ordering;
    }
    if (pageSize) {
      params.page_size = pageSize;
    }
    return await api.get(`${NAMESPACE}/items/`, params);
  },
  create: async (formData) => {
    return await api.post(
      `${NAMESPACE}/items/`,
      formData,
      {},
      { 'Content-Type': 'multipart/form-data' },
    );
  },
  update: async (itemId, organisationId, data) => {
    return await api.patch(
      `${NAMESPACE}/items/${itemId}/?organisation_id=${organisationId}`,
      data,
    );
  },
  safeUpdate: async (itemId, organisationId, data) => {
    return await api.patch(
      `${NAMESPACE}/items/${itemId}/safe-update/?organisation_id=${organisationId}`,
      data,
    );
  },
  remove: async (itemId) => (
    await api.delete(`${NAMESPACE}/items/${itemId}/`)
  ),
  updateOptionImage: async (optionId, organisationId, image) => {
    const formData = new FormData();
    formData.append('image', image);
    return await api.patch(
      `${NAMESPACE}/item-options/${optionId}/?organisation_id=${organisationId}`,
      formData,
      {},
      { 'Content-Type': 'multipart/form-data' },
    );
  },
  fetchAverages: async ({ itemId, researchUUIDs }) => {
    const params = {
      research_uuids: researchUUIDs.join(','),
    };
    if (itemId) {
      params.item_id = itemId;
    }
    return await api.get(`${NAMESPACE}/items/averages/`, params);
  },
};

export const researchItems = {
  list: async ({ researchUUID }) => (
    await api.get(`${NAMESPACE}/research-items/?research_uuid=${researchUUID}`)
  ),
  get: async (researchItemId) => (
    await api.get(`${NAMESPACE}/research-items/${researchItemId}/`)
  ),
  create: async ({ itemId, researchUUID }) => (
    await api.post(`${NAMESPACE}/research-items/`, {
      item_id: itemId,
      research_uuid: researchUUID,
    })
  ),
  remove: async (researchItemId) => (
    await api.delete(`${NAMESPACE}/research-items/${researchItemId}/`)
  ),
  reorder: async (newOrder) => (
    await api.patch(`${NAMESPACE}/research-items/order/`, { new_order: newOrder })
  ),
};

// The invite links of the organisation's self-shared researches (FID-3.6);
// the same shape as the admin's, for ResearchInviteLinkList;
export const researchInviteLinks = {
  list: async ({ researchId, status, page }) => (
    await api.get(`${NAMESPACE}/research-invite-links/`, {
      research_id: researchId,
      status: status || undefined,
      page,
    })
  ),
  create: async ({
    researchId, endDate, title, showOrganisation, maxUses,
  }) => (
    await api.post(`${NAMESPACE}/research-invite-links/`, {
      research_id: researchId,
      end_date: endDate,
      title,
      show_organisation: showOrganisation,
      max_uses: maxUses,
    })
  ),
  update: async (linkId, data) => (
    await api.patch(`${NAMESPACE}/research-invite-links/${linkId}/`, data)
  ),
  delete: async (linkId) => (
    await api.delete(`${NAMESPACE}/research-invite-links/${linkId}/`)
  ),
};

export const researchProposals = {
  list: async ({ organisationId, ...queryParams } = {}) => (
    await api.get(`${NAMESPACE}/research-proposals/`, {
      organisation_id: organisationId,
      ...queryParams,
    })
  ),
  get: async (proposalId) => (
    await api.get(`${NAMESPACE}/research-proposals/${proposalId}/`)
  ),
  accept: async (proposalId) => (
    await api.post(`${NAMESPACE}/research-proposals/${proposalId}/accept/`)
  ),
};


export const researchAnswers = {
  list: async ({ queryParams = {} }) => (
    await api.get(`${NAMESPACE}/research-answers/`, queryParams)
  ),
  // Het opgeschoonde open antwoord. `null` zet het terug op het origineel.
  updateCleanedValue: async (answerId, valueCleaned) => (
    await api.patch(`${NAMESPACE}/research-answers/${answerId}/`, {
      value_cleaned: valueCleaned,
    })
  ),
};

export const researchDashboards = {
  fetchBatchData: async ({ researchUUID, queryParams = {} }) => (
    await api.get(
      `${NAMESPACE}/researches/${researchUUID}/dashboard-data/`,
      queryParams,
    )
  ),
};

export const researchItemSummaries = {
  list: async ({ researchItemId }) => (
    await api.get(`${NAMESPACE}/research-insights/`, {
      research_item_id: researchItemId,
    })
  ),
  listAvailablePrompts: async ({ researchItemId }) => (
    await api.get(`${NAMESPACE}/ai-prompts/`, {
      type: 'research_item_summary',
      research_item_id: researchItemId,
    })
  ),
  create: async ({ researchItemId, promptId, dashboardFilter = null }) => (
    await api.post(`${NAMESPACE}/research-insights/`, {
      research_item_id: researchItemId,
      prompt_id: promptId,
      dashboard_filter: dashboardFilter,
    })
  ),
  status: async (insightId) => (
    await api.get(`${NAMESPACE}/research-insights/${insightId}/status/`)
  ),
};

export const researchSummaries = {
  list: async ({ researchId }) => (
    await api.get(`${NAMESPACE}/research-insights/`, {
      research_id: researchId,
    })
  ),
  listAvailablePrompts: async ({ researchId }) => (
    await api.get(`${NAMESPACE}/ai-prompts/`, {
      type: 'research_level_insight',
      research_id: researchId,
    })
  ),
  create: async ({ researchId, promptId, dashboardFilter = null }) => (
    await api.post(`${NAMESPACE}/research-insights/`, {
      research_id: researchId,
      prompt_id: promptId,
      dashboard_filter: dashboardFilter,
    })
  ),
  status: async (insightId) => (
    await api.get(`${NAMESPACE}/research-insights/${insightId}/status/`)
  ),
};

export const researchTypes = {
  get: async (researchTypeSlug, organisationId) => (
    await api.get(`${NAMESPACE}/research-types/${researchTypeSlug}/`, {
      organisation_id: organisationId,
    })
  ),
  list: async () => (
    await api.get(`${NAMESPACE}/research-types/`)
  ),
};

export const commercialCategories = {
  list: async () => (
    await api.get(`${NAMESPACE}/commercial-categories/`)
  ),
};

export const researchStructureSnapshots = {
  list: async ({ researchUuid }) => (
    await api.get(`${NAMESPACE}/research-structure-snapshots/?research_uuid=${researchUuid}`)
  ),
};

export const researchShareLinks = {
  create: async ({ researchUuid, password, expiresAt }) => (
    await api.post(`${NAMESPACE}/research-share-links/`, {
      research: researchUuid,
      password,
      expires_at: expiresAt,
    })
  ),
};

